import { useState, type FormEvent, type ReactNode } from "react"

import {
  ArchiveBoxIcon,
  ArrowsRightLeftIcon,
  DocumentDuplicateIcon,
  LockClosedIcon,
  PencilIcon,
} from "@heroicons/react/20/solid"
import format from "date-fns/format"
import parseISO from "date-fns/parseISO"
import { Link } from "react-router-dom"

import type {
  SolarDesignVersionDto,
  SolarDesignVersionsDto,
  SolarDesignVersionsResultDto,
} from "~/api/dto/solar-design-versions.dto"
import useSolarDesignVersionCommand from "~/api/hooks/useSolarDesignVersionCommand"
import useSolarDesignVersions from "~/api/hooks/useSolarDesignVersions"
import {
  SOLAR_DESIGN_VERSION_EQUIPMENT_UNAVAILABLE_CODE,
  SOLAR_DESIGN_VERSION_NO_UTILITY_CODE,
  solarProposalErrorCode,
  solarProposalErrorMessages,
} from "~/api/solar-proposal-errors"
import { parseAxiosError } from "~/utils/parseAxiosError"

import { solarSetupStepPath, type SolarSetupStep } from "./solar-setup-paths"

export const SOLAR_DESIGN_VERSION_NAME_MAX_LENGTH = 120
export const DUPLICATE_PRODUCTION_WARNING = `Duplicated, but production couldn't be updated. Retry it from System Design.`
export const SWITCH_VERSION_COPY = `The design, its equipment and production change now. Pricing and savings stay as they are until you refresh them.`

const secondaryButtonClass = `inline-flex items-center justify-center gap-1.5 rounded-md border border-control-border px-3 py-1.5 text-sm font-semibold text-foreground hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50`
const primaryButtonClass = `inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50`
const pillClass = `shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold`

/** "Ver. N · name", what a version is called everywhere it's shown. */
export const versionLabel = (
  version: Pick<SolarDesignVersionDto, `name` | `ordinal`>,
): string =>
  version.name
    ? `Ver. ${version.ordinal} · ${version.name}`
    : `Ver. ${version.ordinal}`

const describeVersion = (version: SolarDesignVersionDto): string =>
  [
    format(parseISO(version.createdAt), `MMM d, yyyy`),
    version.panelCount == null ? null : `${version.panelCount} panels`,
    version.systemSizeKw == null
      ? null
      : `${version.systemSizeKw.toFixed(2)} kW`,
    version.moduleName,
    version.annualProductionKwh == null
      ? null
      : `${version.annualProductionKwh.toLocaleString(`en-US`)} kWh/yr`,
  ]
    .filter(Boolean)
    .join(` · `)

const Notice = ({ children }: { children: ReactNode }): JSX.Element => (
  <p
    role="status"
    className="flex items-start gap-2 rounded-xl border border-border bg-surface p-4 text-sm text-foreground"
  >
    <LockClosedIcon
      aria-hidden="true"
      className="mt-0.5 size-4 shrink-0 text-muted"
    />
    {children}
  </p>
)

/** Where a refusal is fixed: the step that holds what the switch needs. */
const ERROR_FIXES: Record<string, { step: SolarSetupStep; label: string }> = {
  [SOLAR_DESIGN_VERSION_EQUIPMENT_UNAVAILABLE_CODE]: {
    step: `equipment`,
    label: `Open Finance Partner & Equipment`,
  },
  [SOLAR_DESIGN_VERSION_NO_UTILITY_CODE]: {
    step: `utility`,
    label: `Open Utility Company & Rate`,
  },
}

const CommandError = ({
  error,
  proposalId,
}: {
  error: unknown
  proposalId: string
}): JSX.Element => {
  const messages = solarProposalErrorMessages(error)
  const fix = ERROR_FIXES[solarProposalErrorCode(error) ?? ``]
  return (
    <div
      role="alert"
      className="space-y-2 rounded-xl border border-danger/40 bg-danger/10 p-4 text-sm text-foreground"
    >
      {messages.length > 0 ? (
        messages.map((message) => <p key={message}>{message}</p>)
      ) : (
        <p>
          {parseAxiosError(error, `The design version could not be updated.`)}
        </p>
      )}
      {fix ? (
        <Link
          to={solarSetupStepPath(proposalId, fix.step)}
          className="inline-block font-semibold text-primary underline"
        >
          {fix.label}
        </Link>
      ) : null}
    </div>
  )
}

const Confirm = ({
  title,
  body,
  confirmLabel,
  disabled,
  onCancel,
  onConfirm,
}: {
  title: string
  body: string
  confirmLabel: string
  disabled: boolean
  onCancel: () => void
  onConfirm: () => void
}): JSX.Element => (
  <div
    aria-live="polite"
    className="flex flex-col gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4 text-sm text-foreground"
  >
    <p className="font-semibold">{title}</p>
    <p>{body}</p>
    <div className="flex justify-end gap-3">
      <button type="button" className={secondaryButtonClass} onClick={onCancel}>
        Cancel
      </button>
      <button
        type="button"
        className={primaryButtonClass}
        disabled={disabled}
        onClick={onConfirm}
      >
        {confirmLabel}
      </button>
    </div>
  </div>
)

const RenameForm = ({
  version,
  saving,
  onCancel,
  onSave,
}: {
  version: SolarDesignVersionDto
  saving: boolean
  onCancel: () => void
  onSave: (name: string) => void
}): JSX.Element => {
  const [draft, setDraft] = useState(version.name ?? ``)
  const submit = (event: FormEvent): void => {
    event.preventDefault()
    onSave(draft.trim())
  }

  return (
    <form className="flex flex-wrap items-center gap-2" onSubmit={submit}>
      <label className="sr-only" htmlFor={`rename-${version.id}`}>
        Name for Ver. {version.ordinal}
      </label>
      <input
        id={`rename-${version.id}`}
        autoFocus
        value={draft}
        maxLength={SOLAR_DESIGN_VERSION_NAME_MAX_LENGTH}
        placeholder="Leave empty for the default name"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === `Escape`) onCancel()
        }}
        className="min-w-0 flex-1 rounded-md border border-control-border bg-background px-3 py-1.5 text-sm text-foreground"
      />
      <button type="submit" className={primaryButtonClass} disabled={saving}>
        {saving ? `Saving…` : `Save name`}
      </button>
      <button type="button" className={secondaryButtonClass} onClick={onCancel}>
        Cancel
      </button>
    </form>
  )
}

type Pending =
  | { kind: `archive`; version: SolarDesignVersionDto }
  | { kind: `rename`; version: SolarDesignVersionDto }
  | { kind: `switch`; version: SolarDesignVersionDto }

const VersionRow = ({
  version,
  actionsDisabled,
  onPending,
  onDuplicate,
}: {
  version: SolarDesignVersionDto
  /** No write is possible at all: the reason shows above the list. */
  actionsDisabled: boolean
  onPending: (pending: Pending) => void
  onDuplicate: (version: SolarDesignVersionDto) => void
}): JSX.Element => {
  const label = versionLabel(version)
  // A design-team version that can't be used yet can't be copied either.
  const unusable =
    version.requestState === `rejected` || version.requestState === `in_progress`
  const rowReason =
    !version.isActive && !version.canActivate
      ? version.activateBlockedReason
      : null

  return (
    <li
      data-testid="solar-design-version"
      className="flex flex-col gap-3 p-4 text-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-semibold text-foreground">{label}</p>
          <p className="text-muted tabular-nums">{describeVersion(version)}</p>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
          {version.isActive ? (
            <span className={`${pillClass} bg-success/15 text-success`}>
              Active
            </span>
          ) : null}
          {version.requestState === `rejected` ? (
            <span className={`${pillClass} bg-danger/15 text-danger`}>
              Rejected
            </span>
          ) : null}
          {version.requestState === `in_progress` ? (
            <span className={`${pillClass} bg-warning/15 text-foreground`}>
              Design in progress
            </span>
          ) : null}
        </div>
      </div>
      {rowReason && !actionsDisabled ? (
        <p className="text-xs text-muted">{rowReason}</p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {version.isActive ? null : (
          <button
            type="button"
            aria-label={`Switch to ${label}`}
            title={version.activateBlockedReason ?? undefined}
            className={secondaryButtonClass}
            disabled={actionsDisabled || !version.canActivate}
            onClick={() => onPending({ kind: `switch`, version })}
          >
            <ArrowsRightLeftIcon aria-hidden="true" className="size-4" />
            Switch
          </button>
        )}
        <button
          type="button"
          aria-label={`Duplicate ${label}`}
          className={secondaryButtonClass}
          disabled={actionsDisabled || unusable}
          onClick={() => onDuplicate(version)}
        >
          <DocumentDuplicateIcon aria-hidden="true" className="size-4" />
          Duplicate
        </button>
        <button
          type="button"
          aria-label={`Rename ${label}`}
          className={secondaryButtonClass}
          disabled={actionsDisabled}
          onClick={() => onPending({ kind: `rename`, version })}
        >
          <PencilIcon aria-hidden="true" className="size-4" />
          Rename
        </button>
        <button
          type="button"
          aria-label={`Archive ${label}`}
          title={version.archiveBlockedReason ?? undefined}
          className={secondaryButtonClass}
          disabled={actionsDisabled || !version.canArchive}
          onClick={() => onPending({ kind: `archive`, version })}
        >
          <ArchiveBoxIcon aria-hidden="true" className="size-4" />
          Archive
        </button>
      </div>
    </li>
  )
}

const VersionList = ({
  proposalId,
  data,
  viewerCanUpdate,
  onDuplicated,
}: {
  proposalId: string
  data: SolarDesignVersionsDto
  viewerCanUpdate: boolean
  onDuplicated: (result: SolarDesignVersionsResultDto) => void
}): JSX.Element => {
  const command = useSolarDesignVersionCommand()
  const [pending, setPending] = useState<Pending | null>(null)
  const actionsDisabled =
    !viewerCanUpdate ||
    !data.viewerCanUpdate ||
    data.switchBlockedReason !== null ||
    command.isLoading
  const { activeDesignId } = data

  const duplicate = (version: SolarDesignVersionDto): void => {
    setPending(null)
    command.mutate(
      {
        kind: `duplicate`,
        proposalId,
        fromDesignId: version.id,
        expectedActiveDesignId: activeDesignId,
      },
      { onSuccess: onDuplicated },
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {data.switchBlockedReason ? (
        <Notice>{data.switchBlockedReason}</Notice>
      ) : null}
      {command.isError ? (
        <CommandError error={command.error} proposalId={proposalId} />
      ) : null}
      <ul
        aria-label="Design versions"
        className="divide-y divide-border rounded-xl border border-border"
      >
        {data.versions.map((version) =>
          pending?.kind === `rename` && pending.version.id === version.id ? (
            <li key={version.id} className="p-4">
              <RenameForm
                version={version}
                saving={command.isLoading}
                onCancel={() => setPending(null)}
                onSave={(name) =>
                  command.mutate(
                    { kind: `rename`, proposalId, designId: version.id, name },
                    { onSuccess: () => setPending(null) },
                  )
                }
              />
            </li>
          ) : (
            <VersionRow
              key={version.id}
              version={version}
              actionsDisabled={actionsDisabled}
              onPending={setPending}
              onDuplicate={duplicate}
            />
          ),
        )}
      </ul>
      {pending?.kind === `switch` ? (
        <Confirm
          title={`Switch to ${versionLabel(pending.version)}?`}
          body={SWITCH_VERSION_COPY}
          confirmLabel="Switch version"
          disabled={actionsDisabled}
          onCancel={() => setPending(null)}
          onConfirm={() => {
            command.mutate({
              kind: `activate`,
              proposalId,
              designId: pending.version.id,
              expectedActiveDesignId: activeDesignId,
            })
            setPending(null)
          }}
        />
      ) : null}
      {pending?.kind === `archive` ? (
        <Confirm
          title={`Archive ${versionLabel(pending.version)}?`}
          body="It leaves this list and can't be switched to. An administrator can restore it."
          confirmLabel="Archive version"
          disabled={actionsDisabled}
          onCancel={() => setPending(null)}
          onConfirm={() => {
            command.mutate({
              kind: `archive`,
              proposalId,
              designId: pending.version.id,
            })
            setPending(null)
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * The System Design step's Vision design versions (VIS-839): every version
 * with its own equipment and production, the active one marked. A seller who
 * can edit the proposal switches, duplicates (the copy becomes active and
 * opens in the designer), renames and archives them. Each write runs inline
 * and recalculates neither pricing nor savings; the step's out-of-date notice
 * then offers the fixes. A financing lock or an Aurora design leaves the list
 * read-only with the reason.
 */
export const SolarDesignVersions = ({
  proposalId,
  viewerCanUpdate,
  onDuplicated,
  hideWhenEmpty = false,
}: {
  proposalId: string
  viewerCanUpdate: boolean
  /** The copy is the active design now; the step opens the designer on it. */
  onDuplicated: (result: SolarDesignVersionsResultDto) => void
  /** On an Aurora proposal the list shows only when Vision versions exist. */
  hideWhenEmpty?: boolean
}): JSX.Element | null => {
  const query = useSolarDesignVersions(proposalId)

  if (query.data && hideWhenEmpty && query.data.versions.length === 0) {
    return null
  }

  return (
    <section
      aria-label="Design versions"
      data-testid="solar-design-versions"
      className="flex flex-col gap-3"
    >
      <h3 className="text-xs font-semibold tracking-wide text-muted uppercase">
        Versions
      </h3>
      {query.data ? (
        <VersionList
          proposalId={proposalId}
          data={query.data}
          viewerCanUpdate={viewerCanUpdate}
          onDuplicated={onDuplicated}
        />
      ) : query.isError ? (
        <div
          role="alert"
          className="space-y-3 rounded-xl border border-danger/40 bg-danger/10 p-4"
        >
          <p className="text-sm text-foreground">
            {parseAxiosError(
              query.error,
              `The design versions could not be loaded.`,
            )}
          </p>
          <button
            type="button"
            className="text-sm font-semibold text-primary underline"
            onClick={() => void query.refetch()}
          >
            Retry
          </button>
        </div>
      ) : (
        <div
          role="status"
          aria-label="Loading design versions"
          className="h-32 animate-pulse rounded-xl bg-muted"
        />
      )}
    </section>
  )
}
